import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  Loader2,
  Play,
  Pause,
  Power,
  Save,
  Star,
  Plus,
  RefreshCw,
  Shield,
  Activity,
  Target,
  TrendingUp,
  Clock,
  User,
  Settings,
  BarChart3,
  MessageSquare,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import api from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';

// --- Types ---

type AgentStatus = 'draft' | 'onboarding' | 'active' | 'paused' | 'retired';

interface AgentIdentityDetail {
  id: string;
  agent_name: string;
  display_name: string;
  description?: string;
  role?: string;
  status: AgentStatus;
  owner_id?: string;
  total_runs: number;
  success_rate: number;
  avg_quality_score: number;
  last_run_at?: string;
  created_at: string;
  updated_at: string;
  permissions?: string[];
  allowed_tools?: string[];
  guardrails?: string[];
  behavioral_guidelines?: string[];
  prohibited_actions?: string[];
}

interface AgentMetrics {
  total_runs: number;
  success_rate: number;
  avg_quality_score: number;
  avg_latency_ms?: number;
  error_rate?: number;
  runs_by_day?: { date: string; count: number }[];
  quality_trend?: { date: string; score: number }[];
  tool_usage?: { tool: string; count: number }[];
}

interface AgentReview {
  id: string;
  reviewer: string;
  reviewer_id?: string;
  rating: number;
  summary: string;
  good_examples?: string[];
  bad_examples?: string[];
  created_at: string;
}

interface CreateReviewData {
  rating: number;
  summary: string;
  good_examples: string;
  bad_examples: string;
}

// --- Constants ---

const STATUS_COLORS: Record<AgentStatus, string> = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  onboarding: 'bg-blue-100 text-blue-700 border-blue-300',
  active: 'bg-green-100 text-green-700 border-green-300',
  paused: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  retired: 'bg-red-100 text-red-700 border-red-300',
};

// --- Helpers ---

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPercent(value: number | undefined | null): string {
  if (value == null) return '0%';
  return `${(value * 100).toFixed(1)}%`;
}

function formatScore(value: number | undefined | null): string {
  if (value == null) return '0.0';
  return value.toFixed(1);
}

// --- Status Badge ---

function StatusBadge({ status }: { status: AgentStatus }) {
  const colorClass = STATUS_COLORS[status] || STATUS_COLORS.draft;
  return (
    <Badge variant="outline" className={colorClass}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </Badge>
  );
}

// --- Star Rating Component ---

function StarRating({
  rating,
  onChange,
  readonly = false,
}: {
  rating: number;
  onChange?: (rating: number) => void;
  readonly?: boolean;
}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-4 w-4 ${
            star <= rating
              ? 'fill-yellow-400 text-yellow-400'
              : 'text-gray-300'
          } ${!readonly ? 'cursor-pointer hover:text-yellow-400' : ''}`}
          onClick={() => {
            if (!readonly && onChange) {
              onChange(star);
            }
          }}
        />
      ))}
    </div>
  );
}

// --- Overview Tab ---

function OverviewTab({ agent }: { agent: AgentIdentityDetail }) {
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100">
                <Activity className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{agent.total_runs ?? 0}</p>
                <p className="text-xs text-muted-foreground">Total Runs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                <Target className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatPercent(agent.success_rate)}
                </p>
                <p className="text-xs text-muted-foreground">Success Rate</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                <TrendingUp className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {formatScore(agent.avg_quality_score)}
                </p>
                <p className="text-xs text-muted-foreground">Avg Quality Score</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Identity Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Identity Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Agent Name</p>
              <p className="text-sm">{agent.agent_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Display Name</p>
              <p className="text-sm">{agent.display_name}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Role</p>
              <p className="text-sm">{agent.role || 'Not specified'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Owner</p>
              <p className="text-sm">{agent.owner_id || 'Unassigned'}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Created</p>
              <p className="text-sm">{formatDate(agent.created_at)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-muted-foreground">Last Run</p>
              <p className="text-sm">{formatDate(agent.last_run_at)}</p>
            </div>
            {agent.description && (
              <div className="space-y-1 md:col-span-2">
                <p className="text-sm font-medium text-muted-foreground">Description</p>
                <p className="text-sm whitespace-pre-wrap">{agent.description}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Permissions and Guardrails */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Permissions
            </CardTitle>
            <CardDescription>Tools and capabilities this agent can access</CardDescription>
          </CardHeader>
          <CardContent>
            {agent.permissions && agent.permissions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {agent.permissions.map((perm, idx) => (
                  <Badge key={idx} variant="secondary">
                    {perm}
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No permissions configured.</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Guardrails
            </CardTitle>
            <CardDescription>Safety constraints and limitations</CardDescription>
          </CardHeader>
          <CardContent>
            {agent.guardrails && agent.guardrails.length > 0 ? (
              <ul className="space-y-2">
                {agent.guardrails.map((rule, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm">
                    <Shield className="h-3.5 w-3.5 mt-0.5 text-orange-500 shrink-0" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No guardrails configured.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// --- Metrics Tab ---

function MetricsTab({
  agentId,
  metrics,
  loading,
  onRefresh,
}: {
  agentId: string;
  metrics: AgentMetrics | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold">No metrics available</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Metrics will appear once this agent has completed runs.
        </p>
        <Button variant="outline" className="mt-4" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Core Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Total Runs</p>
            <p className="text-3xl font-bold mt-1">{metrics.total_runs}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Success Rate</p>
            <p className="text-3xl font-bold mt-1">
              {formatPercent(metrics.success_rate)}
            </p>
            <Progress
              value={(metrics.success_rate ?? 0) * 100}
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Avg Quality</p>
            <p className="text-3xl font-bold mt-1">
              {formatScore(metrics.avg_quality_score)}
            </p>
            <Progress
              value={(metrics.avg_quality_score ?? 0) * 20}
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm font-medium text-muted-foreground">Error Rate</p>
            <p className="text-3xl font-bold mt-1">
              {metrics.error_rate != null
                ? formatPercent(metrics.error_rate)
                : 'N/A'}
            </p>
            {metrics.error_rate != null && (
              <Progress
                value={(metrics.error_rate ?? 0) * 100}
                className="mt-2 h-2"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Avg Latency */}
      {metrics.avg_latency_ms != null && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Average Latency</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {metrics.avg_latency_ms.toFixed(0)} ms
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tool Usage */}
      {metrics.tool_usage && metrics.tool_usage.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tool Usage</CardTitle>
            <CardDescription>
              Frequency of tool invocations across all runs
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead className="text-right">Invocations</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {metrics.tool_usage.map((tool, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-sm">
                      {tool.tool}
                    </TableCell>
                    <TableCell className="text-right">{tool.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Runs by Day */}
      {metrics.runs_by_day && metrics.runs_by_day.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Runs Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {metrics.runs_by_day.map((entry, idx) => {
                const maxCount = Math.max(
                  ...metrics.runs_by_day!.map((d) => d.count),
                  1
                );
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-20 shrink-0">
                      {entry.date}
                    </span>
                    <div className="flex-1">
                      <div
                        className="h-5 bg-primary/20 rounded"
                        style={{
                          width: `${(entry.count / maxCount) * 100}%`,
                          minWidth: entry.count > 0 ? '8px' : '0',
                        }}
                      />
                    </div>
                    <span className="text-xs font-medium w-8 text-right">
                      {entry.count}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quality Trend */}
      {metrics.quality_trend && metrics.quality_trend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Quality Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {metrics.quality_trend.map((entry, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-20 shrink-0">
                    {entry.date}
                  </span>
                  <div className="flex-1">
                    <Progress value={entry.score * 20} className="h-4" />
                  </div>
                  <span className="text-xs font-medium w-8 text-right">
                    {entry.score.toFixed(1)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// --- Reviews Tab ---

function ReviewsTab({
  agentId,
  reviews,
  loading,
  onRefresh,
  onCreateReview,
}: {
  agentId: string;
  reviews: AgentReview[];
  loading: boolean;
  onRefresh: () => void;
  onCreateReview: () => void;
}) {
  if (loading && reviews.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Reviews ({reviews.length})
        </h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={onCreateReview}>
            <Plus className="h-4 w-4 mr-2" />
            Add Review
          </Button>
        </div>
      </div>

      {reviews.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold">No reviews yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Be the first to review this agent's performance.
          </p>
          <Button className="mt-4" size="sm" onClick={onCreateReview}>
            <Plus className="h-4 w-4 mr-2" />
            Add Review
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <Card key={review.id}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                      <User className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{review.reviewer}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDate(review.created_at)}
                      </p>
                    </div>
                  </div>
                  <StarRating rating={review.rating} readonly />
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <p className="text-sm whitespace-pre-wrap">{review.summary}</p>

                {review.good_examples && review.good_examples.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-green-600 flex items-center gap-1 mb-1">
                      <CheckCircle className="h-3 w-3" />
                      Good Examples
                    </p>
                    <ul className="space-y-1">
                      {review.good_examples.map((ex, idx) => (
                        <li
                          key={idx}
                          className="text-xs text-muted-foreground pl-4 border-l-2 border-green-200"
                        >
                          {ex}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {review.bad_examples && review.bad_examples.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-red-600 flex items-center gap-1 mb-1">
                      <XCircle className="h-3 w-3" />
                      Bad Examples
                    </p>
                    <ul className="space-y-1">
                      {review.bad_examples.map((ex, idx) => (
                        <li
                          key={idx}
                          className="text-xs text-muted-foreground pl-4 border-l-2 border-red-200"
                        >
                          {ex}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Settings Tab ---

function SettingsTab({
  agent,
  saving,
  onSave,
}: {
  agent: AgentIdentityDetail;
  saving: boolean;
  onSave: (data: Partial<AgentIdentityDetail>) => void;
}) {
  const [displayName, setDisplayName] = useState(agent.display_name || '');
  const [description, setDescription] = useState(agent.description || '');
  const [role, setRole] = useState(agent.role || '');
  const [allowedTools, setAllowedTools] = useState(
    (agent.allowed_tools || []).join(', ')
  );
  const [behavioralGuidelines, setBehavioralGuidelines] = useState(
    (agent.behavioral_guidelines || []).join('\n')
  );
  const [prohibitedActions, setProhibitedActions] = useState(
    (agent.prohibited_actions || []).join('\n')
  );

  useEffect(() => {
    setDisplayName(agent.display_name || '');
    setDescription(agent.description || '');
    setRole(agent.role || '');
    setAllowedTools((agent.allowed_tools || []).join(', '));
    setBehavioralGuidelines((agent.behavioral_guidelines || []).join('\n'));
    setProhibitedActions((agent.prohibited_actions || []).join('\n'));
  }, [agent]);

  const handleSave = () => {
    onSave({
      display_name: displayName.trim(),
      description: description.trim() || undefined,
      role: role.trim() || undefined,
      allowed_tools: allowedTools
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      behavioral_guidelines: behavioralGuidelines
        .split('\n')
        .map((g) => g.trim())
        .filter(Boolean),
      prohibited_actions: prohibitedActions
        .split('\n')
        .map((a) => a.trim())
        .filter(Boolean),
    });
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">General Settings</CardTitle>
          <CardDescription>
            Update the agent's display information and role configuration.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="settings-display-name">Display Name</Label>
            <Input
              id="settings-display-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={saving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="settings-description">Description</Label>
            <Textarea
              id="settings-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              disabled={saving}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="settings-role">Role</Label>
            <Input
              id="settings-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              placeholder="e.g., Analyst, Assistant, Reviewer"
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Allowed Tools</CardTitle>
          <CardDescription>
            Comma-separated list of tools this agent is permitted to use.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={allowedTools}
            onChange={(e) => setAllowedTools(e.target.value)}
            placeholder="web_search, code_interpreter, file_reader"
            rows={3}
            disabled={saving}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Behavioral Guidelines</CardTitle>
          <CardDescription>
            One guideline per line. These define how the agent should behave.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={behavioralGuidelines}
            onChange={(e) => setBehavioralGuidelines(e.target.value)}
            placeholder="Always cite sources when providing information&#10;Maintain a professional tone&#10;Ask for clarification when the task is ambiguous"
            rows={5}
            disabled={saving}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prohibited Actions</CardTitle>
          <CardDescription>
            One action per line. These define what the agent must never do.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={prohibitedActions}
            onChange={(e) => setProhibitedActions(e.target.value)}
            placeholder="Never share user credentials&#10;Do not make financial transactions&#10;Avoid generating harmful content"
            rows={5}
            disabled={saving}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
      </div>
    </div>
  );
}

// --- Create Review Dialog ---

function CreateReviewDialog({
  open,
  onOpenChange,
  onCreated,
  agentId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (review: AgentReview) => void;
  agentId: string;
}) {
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState<CreateReviewData>({
    rating: 0,
    summary: '',
    good_examples: '',
    bad_examples: '',
  });

  useEffect(() => {
    if (open) {
      setFormData({
        rating: 0,
        summary: '',
        good_examples: '',
        bad_examples: '',
      });
    }
  }, [open]);

  const handleCreate = async () => {
    if (formData.rating === 0) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a rating.',
        variant: 'destructive',
      });
      return;
    }
    if (!formData.summary.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please provide a review summary.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setCreating(true);
      const payload: Record<string, unknown> = {
        rating: formData.rating,
        summary: formData.summary.trim(),
      };
      const goodExamples = formData.good_examples
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      const badExamples = formData.bad_examples
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
      if (goodExamples.length > 0) payload.good_examples = goodExamples;
      if (badExamples.length > 0) payload.bad_examples = badExamples;

      const response = await api.post(
        '/agent-identities/' + agentId + '/reviews',
        payload
      );
      toast({
        title: 'Review Added',
        description: 'Your review has been submitted successfully.',
      });
      onCreated(response.data);
      onOpenChange(false);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to create review.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Review</DialogTitle>
          <DialogDescription>
            Share your assessment of this agent's performance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>Rating *</Label>
            <StarRating
              rating={formData.rating}
              onChange={(r) => setFormData((prev) => ({ ...prev, rating: r }))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="review-summary">Summary *</Label>
            <Textarea
              id="review-summary"
              placeholder="Describe the agent's performance..."
              value={formData.summary}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, summary: e.target.value }))
              }
              rows={4}
              disabled={creating}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="review-good">Good Examples (one per line)</Label>
            <Textarea
              id="review-good"
              placeholder="Handled complex queries effectively&#10;Cited relevant sources consistently"
              value={formData.good_examples}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  good_examples: e.target.value,
                }))
              }
              rows={3}
              disabled={creating}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="review-bad">Bad Examples (one per line)</Label>
            <Textarea
              id="review-bad"
              placeholder="Sometimes hallucinated dates&#10;Missed edge cases in analysis"
              value={formData.bad_examples}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  bad_examples: e.target.value,
                }))
              }
              rows={3}
              disabled={creating}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- Main Page Component ---

export function AgentIdentityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();

  const [agent, setAgent] = useState<AgentIdentityDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [activeTab, setActiveTab] = useState('overview');

  // Metrics state
  const [metrics, setMetrics] = useState<AgentMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Reviews state
  const [reviews, setReviews] = useState<AgentReview[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [createReviewOpen, setCreateReviewOpen] = useState(false);

  // Fetch agent detail
  const fetchAgent = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/agent-identities/' + id);
      setAgent(response.data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch agent identity.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  // Fetch metrics
  const fetchMetrics = useCallback(async () => {
    if (!id) return;
    try {
      setMetricsLoading(true);
      const response = await api.get('/agent-identities/' + id + '/metrics');
      setMetrics(response.data);
    } catch {
      // Metrics may not be available yet, fail silently
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [id]);

  // Fetch reviews
  const fetchReviews = useCallback(async () => {
    if (!id) return;
    try {
      setReviewsLoading(true);
      const response = await api.get('/agent-identities/' + id + '/reviews');
      const data = response.data;
      if (Array.isArray(data)) {
        setReviews(data);
      } else if (data && Array.isArray(data.reviews)) {
        setReviews(data.reviews);
      } else if (data && Array.isArray(data.items)) {
        setReviews(data.items);
      } else {
        setReviews([]);
      }
    } catch {
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAgent();
  }, [fetchAgent]);

  // Load tab data on tab change
  useEffect(() => {
    if (activeTab === 'metrics' && !metrics && !metricsLoading) {
      fetchMetrics();
    }
    if (activeTab === 'reviews' && reviews.length === 0 && !reviewsLoading) {
      fetchReviews();
    }
  }, [activeTab, metrics, metricsLoading, reviews.length, reviewsLoading, fetchMetrics, fetchReviews]);

  // Status transition actions
  const handleStatusAction = async (action: 'onboard' | 'pause' | 'activate') => {
    if (!id) return;
    try {
      setActionLoading(true);
      const response = await api.post('/agent-identities/' + id + '/' + action);
      setAgent(response.data);
      toast({
        title: 'Status Updated',
        description: `Agent has been ${action === 'onboard' ? 'onboarded' : action === 'pause' ? 'paused' : 'activated'} successfully.`,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : `Failed to ${action} agent.`;
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Save settings
  const handleSave = async (data: Partial<AgentIdentityDetail>) => {
    if (!id) return;
    try {
      setSaving(true);
      const response = await api.put('/agent-identities/' + id, data);
      setAgent(response.data);
      toast({
        title: 'Saved',
        description: 'Agent settings have been updated successfully.',
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to save settings.';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Handle review created
  const handleReviewCreated = (review: AgentReview) => {
    setReviews((prev) => [review, ...prev]);
  };

  // Determine available actions based on status
  const getStatusActions = (status: AgentStatus) => {
    const actions: { label: string; action: 'onboard' | 'pause' | 'activate'; icon: React.ReactNode; variant: 'default' | 'outline' | 'destructive' }[] = [];
    switch (status) {
      case 'draft':
        actions.push({
          label: 'Onboard',
          action: 'onboard',
          icon: <Play className="h-4 w-4 mr-2" />,
          variant: 'default',
        });
        break;
      case 'onboarding':
        actions.push({
          label: 'Activate',
          action: 'activate',
          icon: <Power className="h-4 w-4 mr-2" />,
          variant: 'default',
        });
        break;
      case 'active':
        actions.push({
          label: 'Pause',
          action: 'pause',
          icon: <Pause className="h-4 w-4 mr-2" />,
          variant: 'outline',
        });
        break;
      case 'paused':
        actions.push({
          label: 'Activate',
          action: 'activate',
          icon: <Power className="h-4 w-4 mr-2" />,
          variant: 'default',
        });
        break;
      case 'retired':
        // No actions for retired agents
        break;
    }
    return actions;
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>
            {error || 'Agent identity not found.'}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/agent-identities')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Agent Identities
        </Button>
      </div>
    );
  }

  const statusActions = getStatusActions(agent.status);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <div className="flex items-center gap-2 mb-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/agent-identities')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Bot className="h-6 w-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold">
                  {agent.display_name || agent.agent_name}
                </h1>
                <StatusBadge status={agent.status} />
              </div>
              {agent.role && (
                <p className="text-sm text-muted-foreground">{agent.role}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {statusActions.map((sa) => (
              <Button
                key={sa.action}
                variant={sa.variant}
                size="sm"
                onClick={() => handleStatusAction(sa.action)}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  sa.icon
                )}
                {sa.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col h-full"
        >
          <div className="px-6 border-b">
            <TabsList className="h-10">
              <TabsTrigger value="overview" className="gap-1.5">
                <Info className="h-3.5 w-3.5" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="metrics" className="gap-1.5">
                <BarChart3 className="h-3.5 w-3.5" />
                Metrics
              </TabsTrigger>
              <TabsTrigger value="reviews" className="gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" />
                Reviews
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-1.5">
                <Settings className="h-3.5 w-3.5" />
                Settings
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="flex-1">
            <div className="px-6 py-4">
              <TabsContent value="overview" className="mt-0">
                <OverviewTab agent={agent} />
              </TabsContent>

              <TabsContent value="metrics" className="mt-0">
                <MetricsTab
                  agentId={id!}
                  metrics={metrics}
                  loading={metricsLoading}
                  onRefresh={fetchMetrics}
                />
              </TabsContent>

              <TabsContent value="reviews" className="mt-0">
                <ReviewsTab
                  agentId={id!}
                  reviews={reviews}
                  loading={reviewsLoading}
                  onRefresh={fetchReviews}
                  onCreateReview={() => setCreateReviewOpen(true)}
                />
              </TabsContent>

              <TabsContent value="settings" className="mt-0">
                <SettingsTab
                  agent={agent}
                  saving={saving}
                  onSave={handleSave}
                />
              </TabsContent>
            </div>
          </ScrollArea>
        </Tabs>
      </div>

      {/* Create Review Dialog */}
      <CreateReviewDialog
        open={createReviewOpen}
        onOpenChange={setCreateReviewOpen}
        onCreated={handleReviewCreated}
        agentId={id!}
      />
    </div>
  );
}

export default AgentIdentityDetailPage;
